import {
  ForwardRefRenderFunction, forwardRef, useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useMutation } from '@apollo/client';
import { HomeApplicationStatus } from '../../../interfaces/homeApplication';
import { ADMIN_UPDATE_APARTMENT } from '../../../graphql/admin_mutations';
import useToast from '../../../customHooks/useToast';
import AdminModal from '../../../components/AdminModal';
import InfoIconNew from '../../../svgs/InfoIconNew';
import TextInput from '../../../components/input';
import useForm from '../../../customHooks/useForm';

type Props = {
  onSuccess?: () => void;
  onError?: () => void;
  id: string;
}

export type IHouseApplicationsDetailsMonitor = {
  open: (updateStatus: HomeApplicationStatus) => void;
  close: () => void;
}

const HouseApplicationsDetailsMonitorActions:
 // eslint-disable-next-line no-empty-pattern
 ForwardRefRenderFunction<IHouseApplicationsDetailsMonitor, Props> = ({ id, onSuccess }, ref) => {
   const [show, setShow] = useState(false);
   const [successful, setSuccessful] = useState(false);
   const newStatus = useRef<HomeApplicationStatus>();
   const Toast = useToast();
   const headerTitles: Partial<Record<HomeApplicationStatus, string>> = {
     approved: 'Change to Approved?',
     refunded: 'Refund Transaction?',
     processing: 'Change to Processing?',
   };
   const headerSuccessTitles: Partial<Record<HomeApplicationStatus, string>> = {
     approved: 'Updated Successfully!',
     refunded: 'Refunded Successfully!',
     processing: 'Updated Successfully!',
   };
   const headerSubtitles: Partial<Record<HomeApplicationStatus, string>> = {
     approved: 'Are you sure you want to change this transaction to approved? If you are unsure, cancel.',
     refunded: 'Kindly provide the reason for refunding this transaction.',
     processing: 'Are you sure you want to change this transaction to processing? If you are unsure, cancel.',
   };
   const headerSuccessSubtitles: Partial<Record<HomeApplicationStatus, string>> = {
     approved: 'You have successfully updated this transaction status, the user will be informed and it will be reflected on your dashboard.',
     refunded: 'You have successfully refunded this transaction, the user will be informed and it will be reflected on your dashboard. ',
     processing: 'You have successfully updated this transaction status, the user will be informed and it will be reflected on your dashboard.',
   };
   const buttonTitle: Partial<Record<HomeApplicationStatus, string>> = {
     approved: 'Change To Approved',
     refunded: 'Refund',
     processing: 'Change To Processing',
   };

   const {
     formValues, handleChange, resetForm,
   } = useForm(
     {
       reason: '',
     },
     {},
     Toast,
   );

   const [updateAdminApartment, { loading }] = useMutation<
   { adminUpdateApartmentSearchStatus: any }
   >(ADMIN_UPDATE_APARTMENT, {
     onCompleted() {},
   });

   function closeModal() {
     newStatus.current = undefined;
     setShow(false);
     setSuccessful(false);
     resetForm();
   }

   useImperativeHandle(ref, () => ({
     close() {
       closeModal();
     },
     open(updateStatus) {
       newStatus.current = updateStatus;
       setShow(true);
     },
   }));

   const handleUpdateStatus = async () => {
     try {
       const variables: Record<string, string> = {
         adminUpdateApartmentSearchStatusId: id,
         status: newStatus.current,
       };
       if (formValues.reason) {
         variables.reason = formValues.reason;
       }
       const { data } = await updateAdminApartment({
         variables,
       });
       if (data?.adminUpdateApartmentSearchStatus?.status === 200) {
         setSuccessful(true);
         onSuccess?.();
         Toast.success(`You have successfully updated Application status to ${newStatus.current}`);
         return;
       }
       Toast.error(data?.adminUpdateApartmentSearchStatus?.message ?? 'Couldn\'t update Application Status');
     } catch (error) {
       Toast.error('Couldn\'t update Application Status');
     }
   };

   const title = successful ? headerSuccessTitles : headerTitles;
   const subtitle = successful ? headerSuccessSubtitles : headerSubtitles;
   const buttonTitleText = buttonTitle[newStatus.current];
   return (
     <AdminModal
       headText={title[newStatus.current]}
       subText={subtitle[newStatus.current]}
       actionBtnName={`${buttonTitleText}${loading ? '...' : ''}`}
       handleModalAction={handleUpdateStatus}
       topIcon={<InfoIconNew />}
       isShow={show}
       isSuccess={successful}
       isActionBtnDisabled={loading}
       onClickAway={() => {}}
       onClose={() => closeModal()}
       isLoading={loading}
     >
       {newStatus.current === 'refunded' && !successful && (
       <div>
         <TextInput
           rows={5}
           name="reason"
           value={formValues?.reason}
           multiline
           label="Reason"
           isRequired
           customOnChange={handleChange}
           backgroundColor="#F9F9F9"
         />
       </div>
       )}
     </AdminModal>
   );
 };

export default forwardRef(HouseApplicationsDetailsMonitorActions);
